'use client';

import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import PropTypes from 'prop-types';

/**
 * Component that redirects premium users from checkout to overview
 * Prevents premium users from accessing checkout page after they've already subscribed
 * NON-BLOCKING: Renders children immediately while checking status in background
 */
export default function CheckoutRedirect({ children }) {
  const [user, setUser] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [checksComplete, setChecksComplete] = useState(false);
  
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        
        // Check for cached premium status first
        const cacheKey = `premium_status_${currentUser.uid}`;
        const cachedData = sessionStorage.getItem(cacheKey);
        
        if (cachedData) {
          try {
            const { eligible, timestamp } = JSON.parse(cachedData);
            // Cache is valid for 5 minutes
            if (Date.now() - timestamp < 5 * 60 * 1000) {
              setIsPremium(eligible);
              setChecksComplete(true);
              return;
            }
          } catch (error) {
            console.error('Error parsing cached premium status:', error);
          }
        }
        
        // Check premium status in background
        try {
          const response = await fetch('/api/checkPremiumEligibility', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uid: currentUser.uid }),
          });
          
          if (response.ok) {
            const { eligible } = await response.json();
            setIsPremium(eligible);
            
            // Cache the result
            sessionStorage.setItem(cacheKey, JSON.stringify({
              eligible,
              timestamp: Date.now()
            }));
          }
        } catch (error) {
          console.error('Error checking premium status:', error);
        }
        setChecksComplete(true);
      } else {
        // Not logged in
        setUser(null);
        setChecksComplete(true);
      }
    });
    
    return () => unsubscribe();
  }, []);

  // Premium user on checkout page - redirect to overview
  if (checksComplete && isPremium && window.location.pathname === '/checkout') {
    return <Navigate to="/overview" replace />;
  }
  
  // Render children immediately (non-blocking)
  return children;
}

CheckoutRedirect.propTypes = {
  children: PropTypes.node.isRequired
}; 